import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { seoHead } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";
import { paymentsConfigured } from "@/lib/stripe";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { StayEmbeddedCheckout } from "@/components/StayEmbeddedCheckout";
import { CheckCircle2, CreditCard, Printer } from "lucide-react";

/**
 * Guest invoice. A confirmed stay becomes a payable invoice: room nights, boat
 * or skiff days and anything the lodge added during the stay, itemised with an
 * amount due. Guests reach it from /my-stay or straight from their email link,
 * proving who they are with the same email + confirmation code (no accounts).
 */
export const Route = createFileRoute("/my-stay_/invoice")({
  head: () =>
    seoHead({
      path: "/my-stay/invoice",
      title: "Your Invoice — Coffman Cove Lodging",
      description:
        "View and pay your Coffman Cove Lodging invoice: room nights, boat rental and any extras added during your stay.",
      extraMeta: [{ name: "robots", content: "noindex, nofollow" }],
    }),
  validateSearch: (search: Record<string, unknown>): { email?: string; code?: string } => ({
    email: typeof search['email'] === "string" ? search['email'] : undefined,
    code: typeof search['code'] === "string" ? search['code'] : undefined,
  }),
  component: InvoicePage,
});

type Stay = {
  id: string;
  name: string;
  status: string;
  arrival_date: string | null;
  departure_date: string | null;
  party_size: number | null;
  package: string | null;
  confirmation_code: string;
  created_at: string;
  order_status: string | null;
  order_total_cents: number | null;
  order_items: unknown;
  extra_charges?: unknown;
  extra_charges_total_cents?: number | null;
};

type OrderLine = { name?: string; qty?: number; unit_price_cents?: number };
type ExtraCharge = {
  label?: string;
  amount_cents?: number;
  note?: string | null;
  charge_date?: string | null;
  created_at?: string | null;
};

const money = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(`${value}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

const PAID_STATUSES = ["paid", "captured", "complete", "completed", "fulfilled"];

function InvoicePage() {
  const { email: emailParam, code: codeParam } = Route.useSearch();
  const [email, setEmail] = useState(emailParam ?? "");
  const [code, setCode] = useState(codeParam ?? "");
  const [stay, setStay] = useState<Stay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(emailParam && codeParam));
  const [payOpen, setPayOpen] = useState(false);

  const lookup = useCallback(async (emailValue: string, codeValue: string) => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("lookup_booking", {
      _email: emailValue.trim(),
      _code: codeValue.trim(),
    });
    setLoading(false);
    if (rpcError) {
      setError("We couldn't load your invoice just now. Please try again in a moment.");
      return;
    }
    const row = (data as Stay[] | null)?.[0] ?? null;
    if (!row) {
      setError(
        "No stay found for that email and confirmation code. Check your confirmation email, or call (907) 312-1019.",
      );
      return;
    }
    setStay(row);
  }, []);

  useEffect(() => {
    if (emailParam && codeParam) void lookup(emailParam, codeParam);
  }, [emailParam, codeParam, lookup]);

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!email.trim() || code.trim().length < 6) {
      setError("Enter the email you booked with and your confirmation code.");
      return;
    }
    void lookup(email, code);
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-16 print:py-4">
      <h1 className="text-3xl font-semibold text-foreground sm:text-4xl">Your invoice</h1>
      <p className="mt-3 text-muted-foreground print:hidden">
        Everything on your confirmed stay in one bill — room nights, boat or skiff days and any
        extras we added while you were here.
      </p>

      {!stay && (
        <form
          onSubmit={onSubmit}
          className="mt-8 grid gap-4 rounded-2xl border border-border bg-card/60 p-6 print:hidden"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[var(--color-ember)]"
                placeholder="you@example.com"
                autoComplete="email"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Confirmation code</span>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm tracking-widest outline-none focus:border-[var(--color-ember)]"
                placeholder="ABCD1234"
              />
            </label>
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex h-10 items-center gap-2 rounded-md bg-[var(--color-ember)] px-4 text-sm font-medium text-[var(--color-ember-foreground)] transition hover:brightness-110 disabled:opacity-60"
            >
              {loading ? "Loading…" : "Show my invoice"}
            </button>
          </div>
        </form>
      )}

      {error && (
        <p className="mt-4 rounded-md border border-border bg-card/60 p-4 text-sm text-muted-foreground">
          {error}
        </p>
      )}

      {stay && (
        <Invoice
          stay={stay}
          email={email || emailParam || ""}
          code={code || codeParam || ""}
          payOpen={payOpen}
          onPay={() => setPayOpen(true)}
        />
      )}

      <p className="mt-10 text-sm text-muted-foreground print:hidden">
        Questions about a charge?{" "}
        <a className="underline" href="mailto:booking@coffmancovelodging.com">
          booking@coffmancovelodging.com
        </a>{" "}
        or (907) 312-1019. <Link className="underline" to="/my-stay">Back to my stay</Link>.
      </p>
    </main>
  );
}

function Invoice({
  stay,
  email,
  code,
  payOpen,
  onPay,
}: {
  stay: Stay;
  email: string;
  code: string;
  payOpen: boolean;
  onPay: () => void;
}) {
  const items = Array.isArray(stay.order_items) ? (stay.order_items as OrderLine[]) : [];
  const charges = Array.isArray(stay.extra_charges) ? (stay.extra_charges as ExtraCharge[]) : [];
  const chargesTotal =
    stay.extra_charges_total_cents ?? charges.reduce((sum, c) => sum + (c.amount_cents ?? 0), 0);
  const total = (stay.order_total_cents ?? 0) + chargesTotal;
  const approved = stay.status.toLowerCase() === "approved";
  const paid =
    stay.order_status != null && PAID_STATUSES.includes(stay.order_status.toLowerCase());
  const due = paid ? 0 : total;

  return (
    <section className="mt-8 rounded-2xl border border-border bg-card/60 p-6 print:border-0 print:bg-transparent print:p-0">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-5">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Invoice</p>
          <p className="mt-1 font-mono text-lg text-foreground">CCL-{stay.confirmation_code}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Coffman Cove Lodging · 306 Harbor Ave, Coffman Cove, AK 99918
            <br />
            (907) 312-1019 · booking@coffmancovelodging.com
          </p>
        </div>
        <div className="text-right text-sm">
          <p className="text-muted-foreground">Billed to</p>
          <p className="font-medium text-foreground">{stay.name}</p>
          {email && <p className="text-muted-foreground">{email}</p>}
          <p className="mt-2 text-muted-foreground">
            {formatDate(stay.arrival_date)} – {formatDate(stay.departure_date)}
          </p>
        </div>
      </div>

      <table className="mt-6 w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="pb-2 font-medium">Description</th>
            <th className="pb-2 text-right font-medium">Qty</th>
            <th className="pb-2 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {items.map((line, i) => (
            <tr key={`item-${i}`}>
              <td className="py-2 text-foreground">{line.name ?? "Stay charge"}</td>
              <td className="py-2 text-right tabular-nums text-muted-foreground">
                {line.qty ?? 1}
              </td>
              <td className="py-2 text-right tabular-nums text-foreground">
                {money((line.unit_price_cents ?? 0) * (line.qty ?? 1))}
              </td>
            </tr>
          ))}
          {charges.map((charge, i) => (
            <tr key={`charge-${i}`}>
              <td className="py-2 text-foreground">
                {charge.label ?? "Additional charge"}
                {charge.note ? ` — ${charge.note}` : ""}
                <span className="block text-xs text-muted-foreground">
                  {formatDate((charge.charge_date ?? charge.created_at ?? "").slice(0, 10) || null)}
                </span>
              </td>
              <td className="py-2 text-right tabular-nums text-muted-foreground">1</td>
              <td className="py-2 text-right tabular-nums text-foreground">
                {money(charge.amount_cents ?? 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-5 space-y-1 border-t border-border pt-4 text-sm">
        <p className="flex justify-between">
          <span className="text-muted-foreground">Total</span>
          <span className="tabular-nums text-foreground">{money(total)}</span>
        </p>
        <p className="flex justify-between text-base font-semibold">
          <span className="text-foreground">{paid ? "Paid in full" : "Amount due"}</span>
          <span className="tabular-nums text-foreground">{money(due)}</span>
        </p>
      </div>

      {paid && (
        <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-[var(--color-spruce)]/20 px-3 py-1 text-xs font-medium text-foreground">
          <CheckCircle2 className="h-3.5 w-3.5" /> Payment received — thank you.
        </p>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        Rental prices exclude fuel and any damage to equipment or vessels. All fishing is
        self-guided — you run your own boat or skiff.
      </p>

      {approved && !paid && due > 0 && paymentsConfigured() && (
        <div className="mt-6 rounded-xl border border-[var(--color-ember)]/40 bg-background/40 p-4 print:hidden">
          <h2 className="text-sm font-semibold text-foreground">Pay this invoice</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pay {money(due)} by card. Your receipt is emailed as soon as the payment clears, and
            you can still settle by check or cash at the dock if you'd rather.
          </p>
          {payOpen ? (
            <div className="mt-4">
              <PaymentTestModeBanner />
              <StayEmbeddedCheckout
                email={email}
                code={code}
                returnUrl={`${window.location.origin}/checkout/return?stay=1`}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={onPay}
              className="mt-4 inline-flex h-10 items-center gap-2 rounded-md bg-[var(--color-ember)] px-4 text-sm font-medium text-[var(--color-ember-foreground)] transition hover:brightness-110"
            >
              <CreditCard className="h-4 w-4" />
              Pay {money(due)} now
            </button>
          )}
        </div>
      )}

      {!approved && (
        <p className="mt-6 text-sm text-muted-foreground">
          Your dates aren't confirmed yet, so this is an estimate — nothing is due until we
          confirm.
        </p>
      )}

      <button
        type="button"
        onClick={() => window.print()}
        className="mt-6 inline-flex h-9 items-center gap-2 rounded-md border border-border px-3 text-sm text-muted-foreground transition hover:text-foreground print:hidden"
      >
        <Printer className="h-4 w-4" /> Print or save as PDF
      </button>
    </section>
  );
}
